/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One focus trap for every dialog, sheet and modal on the site.
 *
 * Until 2026-10-14 the admin had this hook (then in src/pages/admin/hooks/)
 * and the five public modals — EstimateKwhModal, SignupModal, FilterSheet,
 * SortSheet and InstallerRequestSheet — each carried a copy of the selector
 * and the Tab-cycling keydown handler. Where those copies differed they did so
 * on purpose (a portal that mounts a frame late, a dialog that takes the focus
 * itself so a phone keeps its keyboard down, a roving listbox), so the
 * differences are options here and every caller traps exactly as it did.
 */

import { useEffect, useRef } from 'react';

/**
 * What Tab can land on: the admin's list and the public modals' list in one
 * (`[href]` covers the admin's `a[href]`). A caller narrows it with
 * `tabbable`; the admin's dialogs keep only enabled, rendered stops.
 */
export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface FocusTrapOptions {
  /**
   * Where the trap listens.
   *
   * - `'container'` (default — the admin's dialogs): on the element itself,
   *   which must be mounted when the trap opens. The keys are only heard while
   *   the focus is inside, so: with nothing to focus the container takes the
   *   focus itself, a Tab with nowhere to go stays put, Escape's default is
   *   cancelled, and only enabled, rendered stops count.
   * - `'document'` (the public modals and sheets): on the whole document, with
   *   the element looked up at every key — they mount through a portal, maybe
   *   a frame after `isOpen` flips — so a Tab or an Escape is theirs wherever
   *   the focus is.
   */
  scope?: 'container' | 'document';
  /**
   * Where the focus goes back to on close: this element when it is set (iOS
   * Safari does not focus a tapped button, so `document.activeElement` is the
   * body there), else whatever had the focus when the trap opened.
   */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /** Narrows the stops FOCUSABLE_SELECTOR finds (replaces the container scope's own rule). */
  tabbable?: (el: HTMLElement) => boolean;
  /** What takes the focus on open: the first stop (default), the container itself, or the pick of a function. */
  initialFocus?: 'first' | 'container' | ((container: HTMLElement) => HTMLElement | null);
  /** Focus inside the effect itself; by default a frame later, once a portal or an entrance animation has settled. */
  focusNow?: boolean;
  /**
   * Besides the two edges — Tab on the last stop, Shift+Tab on the first —
   * which focus a Tab brings back in:
   *
   * - `'outside'` (default): focus anywhere outside the container;
   * - `'off-stop'`: focus on anything that is not one of the stops, the
   *   container itself included (a dialog that takes the focus itself);
   * - `'none'`: nothing — from anywhere else, Tab is the browser's.
   */
  recapture?: 'outside' | 'off-stop' | 'none';
  /** Replaces the Tab rule (a roving listbox). Called with at least one stop. */
  onTab?: (e: KeyboardEvent, stops: HTMLElement[], container: HTMLElement) => void;
}

/**
 * The container scope's stops: enabled, in the tab order, and rendered (an
 * element in a collapsed section has no offsetParent). Exported for the
 * admin's dialog shell, which picks its own first stop (AdminDialog.tsx).
 */
export const isRenderedStop = (el: HTMLElement): boolean =>
  !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null;

/**
 * Trap keyboard focus inside `ref` while `isOpen` is true.
 *
 * - On open: remembers where the focus was and moves it into the container
 *   (`initialFocus`, a frame later unless `focusNow`).
 * - While open: Tab and Shift+Tab cycle through the stops; Escape calls
 *   `onEscape` — the latest one passed, so an inline arrow does not re-run
 *   the effect (which would steal the focus from a field being typed in).
 * - On close or unmount: gives the focus back (`returnFocusRef`, else the
 *   element that had it on open).
 */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape?: () => void,
  options: FocusTrapOptions = {}
): void {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  // Read when the trap opens, like the handlers each caller used to build
  // inside its own effect: a fresh options object per render re-runs nothing.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const { returnFocusRef } = options;

  useEffect(() => {
    if (!isOpen) return;
    const {
      scope = 'container',
      tabbable,
      initialFocus = 'first',
      focusNow = false,
      recapture = 'outside',
      onTab,
    } = optionsRef.current;
    const inContainer = scope === 'container';
    const mounted = ref.current;
    if (inContainer && !mounted) return;
    const containerNow = (): HTMLElement | null => (inContainer ? mounted : ref.current);
    const keep = tabbable ?? (inContainer ? isRenderedStop : undefined);
    const stopsIn = (container: HTMLElement | null): HTMLElement[] => {
      const found = Array.from(container?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []);
      return keep ? found.filter(keep) : found;
    };

    const returnTo =
      returnFocusRef?.current ??
      (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const cb = onEscapeRef.current;
        if (cb) {
          if (inContainer) e.preventDefault();
          cb();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const container = containerNow();
      const stops = stopsIn(container);
      if (!container || stops.length === 0) {
        if (inContainer) e.preventDefault();
        return;
      }
      if (onTab) {
        onTab(e, stops, container);
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside =
        recapture === 'none'
          ? true
          : recapture === 'off-stop'
            ? active !== null && stops.includes(active)
            : !!active && container.contains(active);
      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const focusInitial = () => {
      const container = containerNow();
      const target =
        initialFocus === 'container'
          ? container
          : typeof initialFocus === 'function'
            ? container && initialFocus(container)
            : (stopsIn(container)[0] ?? null);
      if (target) {
        target.focus();
      } else if (inContainer && container) {
        // Somewhere for the keys to be heard.
        container.setAttribute('tabindex', '-1');
        container.focus();
      }
    };

    const keyTarget: EventTarget = inContainer ? (mounted as HTMLElement) : document;
    const listener = (e: Event) => onKeyDown(e as KeyboardEvent);
    keyTarget.addEventListener('keydown', listener);
    let frame: number | null = null;
    if (focusNow) focusInitial();
    else frame = requestAnimationFrame(focusInitial);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      keyTarget.removeEventListener('keydown', listener);
      if (returnTo && typeof returnTo.focus === 'function') {
        try {
          returnTo.focus();
        } catch {
          /* noop */
        }
      }
    };
  }, [ref, isOpen, returnFocusRef]);
}
